import type { TableShape } from './mapGeometry';

export const CHAIR_HALF_RATIO = 0.15;
export const CHAIR_GAP_RATIO = 0.1;
export const CHAIR_SLOT_FILL = 0.4;

export interface TableChair {
    x: number;
    y: number;
    half: number;
}

/** Mirrored by james.rn.shared: the two must place the same chairs for the same table. */
export function tableChairs(
    width: number,
    height: number,
    seats: number,
    shape: TableShape
): TableChair[] {
    if (seats <= 0) return [];
    const base = Math.min(width, height);
    const gap = base * CHAIR_GAP_RATIO;
    const halfOf = (slot: number) => Math.min(base * CHAIR_HALF_RATIO, slot * CHAIR_SLOT_FILL);

    if (shape !== 'rectangle') {
        const r = base / 2;
        const isSquare = shape === 'square';
        const half = halfOf(isSquare ? (4 * base) / seats : (2 * Math.PI * (r + gap)) / seats);
        return Array.from({ length: seats }, (_, i) => {
            const angle = -Math.PI / 2 + (i * 2 * Math.PI) / seats;
            const cos = Math.cos(angle);
            const sin = Math.sin(angle);
            const edge = isSquare ? r / Math.max(Math.abs(cos), Math.abs(sin)) : r;
            const distance = edge + gap + half;
            return { x: cos * distance, y: sin * distance, half };
        });
    }

    if (height > width) {
        return tableChairs(height, width, seats, shape).map((c) => ({
            x: c.y,
            y: c.x,
            half: c.half,
        }));
    }

    const side = Math.max(0, seats - 2);
    const top = Math.ceil(side / 2);
    const bottom = side - top;
    const half = halfOf(top > 0 ? width / (top + 1) : height);
    const sideY = height / 2 + gap + half;
    const endX = width / 2 + gap + half;
    const row = (count: number, y: number): TableChair[] =>
        Array.from({ length: count }, (_, i) => ({
            x: -width / 2 + (width / (count + 1)) * (i + 1),
            y,
            half,
        }));
    const left = { x: -endX, y: 0, half };
    const right = { x: endX, y: 0, half };
    return [...row(top, -sideY), ...row(bottom, sideY), left, ...(seats === 1 ? [] : [right])];
}
